import { fromLonLat } from 'ol/proj';
import { map } from './Map.js';
import { GEOSERVER_WFS_URL, HIDDEN_LAYER_NAMES } from './Config.js';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import { getlayerByName } from './CustomFunctions.js';

// === إعدادات Nominatim API ===
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';
// نطاق البحث: فلسطين وإسرائيل والأراضي المحتلة
const VIEWBOX = '34.0,29.5,36.0,33.5'; // west,south,east,north
const MAP_PROJECTION = 'EPSG:28191';

// LGU GeoServer Config
const LGU_LAYER = 'DataBase:LGU';
const WFS_URL = GEOSERVER_WFS_URL; // Secure proxy from Config.js

// ✅ طبقة نتائج البحث العلوية (Highlight)
const topSearchHighlightSource = new VectorSource();
const topSearchHighlightLayer = new VectorLayer({
  source: topSearchHighlightSource,
  style: new Style({
    stroke: new Stroke({ color: "rgba(0, 247, 255, 1)", width: 3 }),
    fill: new Fill({ color: "rgba(0, 247, 255, 0.2)" }),
  }),
});
// Attach to window so Identify.js can clear it
window.topSearchHighlightSource = topSearchHighlightSource;
map.addLayer(topSearchHighlightLayer);

// عناصر سطح المكتب
const desktopForm = document.getElementById('topSearchForm');
const desktopInput = document.getElementById('topSearchInput');
const desktopBtn = document.getElementById('topSearchBtn');

// عناصر الجوال
const mobileForm = document.getElementById('mobileSearchForm');
const mobileInput = document.getElementById('mobileSearchInput');
const mobileBtn = null; // زر البحث المستقل أُزيل — البحث يتم بالـ Enter أو أيقونة البحث الديناميكية

// حاويات الاقتراحات
let desktopSuggBox = document.getElementById('topSearchSuggestions');
let mobileSuggBox = document.getElementById('mobileSearchSuggestions');

// إنشاء حاويات الاقتراحات إذا لم تكن موجودة
if (!desktopSuggBox && desktopForm) {
  desktopSuggBox = document.createElement('div');
  desktopSuggBox.id = 'topSearchSuggestions';
  desktopSuggBox.className = 'top-search-suggestions';
  desktopForm.appendChild(desktopSuggBox);
}

if (!mobileSuggBox && mobileForm) {
  mobileSuggBox = document.createElement('div');
  mobileSuggBox.id = 'mobileSearchSuggestions';
  mobileSuggBox.className = 'mobile-search-suggestions';
  mobileForm.appendChild(mobileSuggBox);
}

// ====== وظائف Nominatim API ======
async function fetchSuggestions(query) {
  if (!query || query.length < 2) return [];

  const url = `${NOMINATIM_BASE}/search?` + new URLSearchParams({
    q: query,
    format: 'json',
    addressdetails: 1,
    limit: 10,
    countrycodes: 'ps,il',
    viewbox: VIEWBOX,
    bounded: 1,
    'accept-language': 'ar,en,he'
  });

  try {
    const res = await fetch(url, {
      headers: {
        'User-Agent': 'GeoPST/1.0 (https://geopst.com)'
      }
    });
    if (!res.ok) throw new Error('Nominatim request failed');
    const data = await res.json();

    // تحويل النتائج لصيغة موحدة
    return data.map(item => ({
      name: item.display_name,
      shortName: formatShortName(item),
      lat: parseFloat(item.lat),
      lon: parseFloat(item.lon),
      type: item.type,
      importance: item.importance
    }));
  } catch (e) {
    console.error('Nominatim search error:', e);
    return [];
  }
}

/**
 * جلب الاقتراحات من طبقة LGU الخاصة بـ GeoServer
 */
async function fetchLGUSuggestions(query) {
  if (!query || query.length < 2) return [];

  // CQL Filter للبحث في الاسم العربي والإنجليزي
  const cql = `CommunityN ILIKE '%${query}%' OR Communit_1 ILIKE '%${query}%'`;

  const params = new URLSearchParams({
    service: 'WFS',
    version: '1.1.0',
    request: 'GetFeature',
    typeName: LGU_LAYER,
    outputFormat: 'application/json',
    cql_filter: cql,
    maxFeatures: 5,
    srsName: 'EPSG:4326' // طلب الإحداثيات بـ Lat/Lon لتوحيد المعالجة مع Nominatim
  });

  try {
    const fetchFn = window.secureFetch || fetch;
    const res = await fetchFn(`${WFS_URL}?${params}`);
    if (!res.ok) return [];

    // GeoServer may return XML error even with HTTP 200
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('json')) return [];

    const data = await res.json();

    return (data.features || []).map(f => {
      const p = f.properties;
      const coords = f.geometry.coordinates; // [lon, lat]
      return {
        name: `${p.CommunityN} (${p.Communit_1})`,
        shortName: p.CommunityN || p.Communit_1,
        lat: coords[1],
        lon: coords[0],
        type: 'lgu',
        importance: 1.0 // أولوية قصوى للنتائج المحلية
      };
    });
  } catch (e) {
    // Silently fail — Nominatim results are the primary source
    return [];
  }
}

// تنسيق اسم قصير للعرض - بدون اسم الدولة
function formatShortName(item) {
  // خذ الاسم الكامل وأزل اسم الدولة (آخر جزء بعد الفاصلة)
  const parts = item.display_name.split(',').map(p => p.trim());

  // أزل آخر جزء (الدولة)
  if (parts.length > 1) {
    parts.pop();
  }

  return parts.join('، ');
}

// زوم للموقع المحدد
function zoomToLocation(lat, lon) {
  try {
    // تحويل من WGS84 إلى إحداثيات الخريطة
    const coords = fromLonLat([lon, lat], MAP_PROJECTION);

    map.getView().animate({
      center: coords,
      zoom: 11,
      duration: 800
    });
  } catch (e) {
    console.error('Zoom error:', e);
    window.showErrorModal?.('حدث خطأ أثناء الانتقال للموقع.');
  }
}

// ====== فئة البحث الموحدة ======
class SearchHandler {
  constructor(form, input, button, suggestionBox, suggestionClass) {
    this.form = form;
    this.input = input;
    this.button = button;
    this.suggestionBox = suggestionBox;
    this.suggestionClass = suggestionClass;
    this.debounceTimer = null;
    this.activeIndex = -1;
    this.suggestions = [];

    this.initializeEvents();
    this.setupDelegation();
  }

  setupDelegation() {
    // ⚡ Level 3: Event Delegation for suggestions
    this.suggestionBox.addEventListener('mousedown', (e) => {
      const itemEl = e.target.closest(`.${this.suggestionClass}`);
      if (itemEl) {
        e.preventDefault();
        const idx = parseInt(itemEl.getAttribute('data-idx'));
        const item = this.suggestions[idx];
        if (item) {
          this.input.value = item.shortName;
          this.hideSuggestions();
          zoomToLocation(item.lat, item.lon);
        }
      }
    });
  }

  async runSearch() {
    const q = (this.input.value || '').trim();
    if (!q) return;

    // ✅ 7-Digit Postal Code Logic
    const isNumeric = /^\d+$/.test(q);
    const isPostalCode = /^\d{7}$/.test(q);

    if (isNumeric) {
      if (!isPostalCode) {
        window.showErrorModal?.('الرمز البريدي يجب أن يكون مكوناً من 7 خانات رقمية (مثال: 6290145).');
        return;
      }
      this.fetchBuildingByPostalCode(q);
      return;
    }

    try {
      window.showLoading?.();
      const results = await fetchSuggestions(q);
      this.hideSuggestions();

      if (results.length > 0) {
        // زوم لأول نتيجة
        zoomToLocation(results[0].lat, results[0].lon);
      } else {
        window.showErrorModal?.('لا توجد نتائج مطابقة لهذا البحث.');
      }
    } catch (e) {
      console.error(e);
      window.showErrorModal?.('حدث خطأ أثناء البحث.');
    } finally {
      window.hideLoading?.();
    }
  }

  /**
   * البحث عن مبنى عبر الرمز البريدي
   */
  async fetchBuildingByPostalCode(postalCode) {
    this.hideSuggestions();
    topSearchHighlightSource.clear(); // مسح أي تحديد سابق
    if (window.identifyHighlightSource) window.identifyHighlightSource.clear();
    window.showLoading?.();

    const params = new URLSearchParams({
      service: 'WFS',
      version: '1.1.0',
      request: 'GetFeature',
      typeName: LGU_LAYER.split(':')[0] + ':building', // DataBase:building
      outputFormat: 'application/json',
      cql_filter: `postal_cod='${postalCode}'`,
      maxFeatures: 1
    });

    try {
      // 🔒 استخدام secureFetch للمرور عبر بروكسي الـ Backend مع التوكن
      const fetchFn = window.secureFetch || fetch;
      const res = await fetchFn(`${WFS_URL}?${params}`);
      
      if (!res.ok) throw new Error('WFS request failed');
      const data = await res.json();

      if (data.features && data.features.length > 0) {
        const featureData = data.features[0];
        
        // تحويل الـ JSON إلى OL Feature وعرضه في طبقة التحديد
        const GeoJSON = (await import('ol/format/GeoJSON')).default;
        const feature = new GeoJSON().readFeature(featureData);
        topSearchHighlightSource.addFeature(feature);

        // التكبير للمبنى
        const extent = feature.getGeometry().getExtent();
        map.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          maxZoom: 18,
          duration: 1000
        });

        // ⚡ Turn OFF Parcel layer ONLY if no other UI layers are active
        const allLayers = map.getLayers().getArray();
        let activeUiLayersCount = 0;
        
        allLayers.forEach(layer => {
          const name = layer.get('name');
          if (name && layer.getVisible() && 
              layer.get('internal') !== true && 
              layer.get('uiHidden') !== true && 
              !HIDDEN_LAYER_NAMES.includes(name)) {
            activeUiLayersCount++;
          }
        });

        const parcelLayer = getlayerByName('Parcel');
        // If Parcel is visible and it's the ONLY active UI layer, turn it off
        if (parcelLayer && parcelLayer.getVisible() && activeUiLayersCount === 1) {
          parcelLayer.setVisible(false);
        }

        const buildingsLayer = getlayerByName('Buildings');
        if (buildingsLayer) buildingsLayer.setVisible(true);
        
        // إغلاق أي نوافذ مفتوحة مسبقاً للجوال
        window.closeSidebarOnMobile?.();
      } else {
        window.showErrorModal?.(`لم يتم العثور على أي مبنى مسجل بالرمز البريدي: ${postalCode}`);
      }
    } catch (e) {
      console.error('Postal search error:', e);
      window.showErrorModal?.('حدث خطأ أثناء الاتصال بسيرفر البيانات. يرجى المحاولة لاحقاً.');
    } finally {
      window.hideLoading?.();
    }
  }

  showSuggestions(list) {
    this.suggestionBox.innerHTML = '';
    this.suggestions = list;

    if (!list?.length) {
      this.suggestionBox.style.display = 'none';
      return;
    }

    list.forEach((item, idx) => {
      const div = document.createElement('div');
      div.className = this.suggestionClass;
      div.setAttribute('data-idx', String(idx));

      // أيقونة حسب النوع
      const icon = getTypeIcon(item.type);
      div.innerHTML = `
        <i class="${icon}" style="margin-left: 8px; color: var(--primary); font-size: 12px;"></i>
        <span>${item.shortName}</span>
      `;

      this.suggestionBox.appendChild(div);
    });

    this.activeIndex = -1;
    this.suggestionBox.style.display = 'block';
  }

  hideSuggestions() {
    this.suggestionBox.style.display = 'none';
    this.suggestionBox.innerHTML = '';
    this.activeIndex = -1;
    this.suggestions = [];
  }

  async handleSuggest() {
    const v = (this.input.value || '').trim();

    // ✅ Skip suggestions if it looks like a postal code (7 digits)
    if (v.length < 2 || /^\d{7}$/.test(v)) {
      this.hideSuggestions();
      return;
    }
    try {
      // ⚡ البحث في المصدرين معاً (Nominatim + GeoServer LGU)
      const [lguResults, globalResults] = await Promise.all([
        fetchLGUSuggestions(v),
        fetchSuggestions(v)
      ]);

      // دمج النتائج مع إعطاء الأولوية للـ LGU
      const combined = [...lguResults, ...globalResults];
      this.showSuggestions(combined);
    } catch (e) {
      console.error(e);
      this.hideSuggestions();
    }
  }

  initializeEvents() {
    if (!this.form || !this.input) return;

    // إرسال النموذج
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.runSearch();
    });

    // الكتابة في حقل البحث
    this.input.addEventListener('input', () => {
      const q = (this.input.value || '').trim();
      if (!q) {
        // ⚡ Clear highlight and suggestions when input is emptied
        topSearchHighlightSource.clear();
        if (window.identifyHighlightSource) window.identifyHighlightSource.clear();
        this.hideSuggestions();
        return;
      }

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.handleSuggest(), 300);
    });

    // التنقل بلوحة المفاتيح
    this.input.addEventListener('keydown', (e) => {
      const visible = this.suggestionBox.style.display === 'block';
      const items = Array.from(this.suggestionBox.querySelectorAll(`.${this.suggestionClass}`));

      if (visible && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        if (!items.length) return;

        if (e.key === 'ArrowDown') this.activeIndex = (this.activeIndex + 1) % items.length;
        if (e.key === 'ArrowUp') this.activeIndex = (this.activeIndex - 1 + items.length) % items.length;

        items.forEach((el, i) => el.classList.toggle('active', i === this.activeIndex));
      } else if (visible && e.key === 'Enter' && this.activeIndex >= 0) {
        e.preventDefault();
        const item = this.suggestions[this.activeIndex];
        if (item) {
          this.input.value = item.shortName;
          this.hideSuggestions();
          zoomToLocation(item.lat, item.lon);
        }
      } else if (e.key === 'Escape') {
        this.hideSuggestions();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.runSearch();
      }
    });

    // زر البحث
    this.button?.addEventListener('click', (e) => {
      e.preventDefault();
      this.runSearch();
    });

    // إغلاق الاقتراحات عند النقر خارجها
    document.addEventListener('click', (e) => {
      if (!this.form.contains(e.target)) {
        this.hideSuggestions();
      }
    });
  }
}

// أيقونة حسب نوع المكان
function getTypeIcon(type) {
  const icons = {
    'lgu': 'fas fa-map-marked-alt', // أيقونة مميزة للمجتمعات المحلية
    'city': 'fas fa-city',
    'town': 'fas fa-building',
    'village': 'fas fa-home',
    'suburb': 'fas fa-map-marker-alt',
    'neighbourhood': 'fas fa-map-pin',
    'hamlet': 'fas fa-house-user',
    'residential': 'fas fa-home',
    'administrative': 'fas fa-landmark'
  };
  return icons[type] || 'fas fa-map-marker-alt';
}

// ====== تهيئة البحث ======
document.addEventListener('DOMContentLoaded', function () {
  // بحث سطح المكتب
  if (desktopForm && desktopInput && desktopBtn && desktopSuggBox) {
    new SearchHandler(desktopForm, desktopInput, desktopBtn, desktopSuggBox, 'top-search-suggestion');
  }

  // بحث الجوال — الزر اختياري (null)
  if (mobileForm && mobileInput && mobileSuggBox) {
    new SearchHandler(mobileForm, mobileInput, mobileBtn, mobileSuggBox, 'mobile-search-suggestion');
  }
});
